/**
 * LandingPage.jsx — CyberExchange public-facing landing page.
 *
 * Self-contained: has its own navbar + all styles as an injected <style> block.
 * Uses only React hooks and browser APIs — no MUI dependency on this page.
 * Featuring professional custom SVG vector icons instead of casual emojis.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────
   SLEEK VECTOR ICONS
───────────────────────────────────────────── */
function Icon({ name, className = '', size = 24, color }) {
  const props = {
    className,
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color || 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  switch (name) {
    case 'shield':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      );
    case 'user':
      return (
        <svg {...props}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      );
    case 'briefcase':
      return (
        <svg {...props}>
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      );
    case 'cpu':
      return (
        <svg {...props}>
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2"/>
          <rect x="9" y="9" width="6" height="6"/>
          <line x1="9" y1="1" x2="9" y2="4"/>
          <line x1="15" y1="1" x2="15" y2="4"/>
          <line x1="9" y1="20" x2="9" y2="23"/>
          <line x1="15" y1="20" x2="15" y2="23"/>
          <line x1="20" y1="9" x2="23" y2="9"/>
          <line x1="20" y1="15" x2="23" y2="15"/>
          <line x1="1" y1="9" x2="4" y2="9"/>
          <line x1="1" y1="15" x2="4" y2="15"/>
        </svg>
      );
    case 'scale':
      return (
        <svg {...props}>
          <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1"/>
          <path d="M18 8h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4"/>
          <path d="M16 12h8"/>
        </svg>
      );
    case 'mail':
      return (
        <svg {...props}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      );
    case 'lock':
      return (
        <svg {...props}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      );
    case 'alert':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      );
    case 'sun':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/>
          <line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      );
    case 'moon':
      return (
        <svg {...props}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      );
    case 'globe':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      );
    case 'tool':
      return (
        <svg {...props}>
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      );
    case 'file-text':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      );
    case 'signature':
      return (
        <svg {...props}>
          <path d="M22 11V3a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4"/>
          <path d="M12 11h8"/>
          <path d="M12 15h8"/>
          <path d="M12 19h5"/>
          <path d="M7 6v6"/>
          <path d="M7 16a2 2 0 0 0 2-2V8a2 2 0 0 0-4 0v6a4 4 0 0 0 8 0"/>
        </svg>
      );
    case 'bot':
      return (
        <svg {...props}>
          <rect x="3" y="11" width="18" height="10" rx="2"/>
          <circle cx="12" cy="5" r="2"/>
          <path d="M12 7v4"/>
          <line x1="8" y1="16" x2="8.01" y2="16"/>
          <line x1="16" y1="16" x2="16.01" y2="16"/>
        </svg>
      );
    case 'target':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
          <circle cx="12" cy="12" r="2"/>
        </svg>
      );
    case 'dollar':
      return (
        <svg {...props}>
          <line x1="12" y1="1" x2="12" y2="23"/>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      );
    case 'trending-up':
      return (
        <svg {...props}>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      );
    case 'key':
      return (
        <svg {...props}>
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.778zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
        </svg>
      );
    case 'ban':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
        </svg>
      );
    case 'clock':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      );
    case 'activity':
      return (
        <svg {...props}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      );
    case 'search':
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      );
    case 'bank':
      return (
        <svg {...props}>
          <line x1="3" y1="22" x2="21" y2="22"/>
          <line x1="6" y1="18" x2="6" y2="11"/>
          <line x1="10" y1="18" x2="10" y2="11"/>
          <line x1="14" y1="18" x2="14" y2="11"/>
          <line x1="18" y1="18" x2="18" y2="11"/>
          <path d="M12 2L2 7v4h20V7L12 2z"/>
        </svg>
      );
    default:
      return null;
  }
}

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const TYPEWRITER_PHRASES = [
  'Verified Vendors',
  'Anonymous Deals',
  'Secure Contracts',
  'KYC-Backed Trust',
];

const STEPS = [
  {
    step: '01',
    icon: 'user',
    title: 'Register & Verify',
    desc: 'Verify identity instantly via official registries (PAN, Aadhaar, GSTIN). Live verification activates your profile immediately.',
    color: '#00d4ff',
  },
  {
    step: '02',
    icon: 'file-text',
    title: 'Post Requirements',
    desc: 'Post cybersecurity needs, budget, and compliance rules. Our secure matching engine alerts qualified vendors in seconds.',
    color: '#7c3aed',
  },
  {
    step: '03',
    icon: 'users',
    title: 'Bid Anonymously',
    desc: 'Submit anonymized bids. Documents are dynamically stripped of company details to conceal your identity completely.',
    color: '#10b981',
  },
  {
    step: '04',
    icon: 'signature',
    title: 'Sign & Release',
    desc: 'Sign digital contracts securely. Escrow holds deposits safely, releasing funds only as milestones are approved.',
    color: '#f59e0b',
  },
];

const FEATURES = [
  {
    icon: 'user',
    title: 'Anonymous Identity Engine',
    desc: 'Conceals company details and user profiles completely until digital contract execution.',
    accent: '#00d4ff',
  },
  {
    icon: 'bot',
    title: 'AI Document Anonymizer',
    desc: 'Automatically scans and strips metadata, emails, and corporate markers from uploaded docs.',
    accent: '#7c3aed',
  },
  {
    icon: 'check',
    title: 'Multi-Layer KYC Verification',
    desc: 'Robust pre-verification across corporate registries eliminates bad actors.',
    accent: '#10b981',
  },
  {
    icon: 'target',
    title: 'Precision Skill Matching',
    desc: 'Smart scoring pairs requirements with the highest-rated cybersecurity vendors instantly.',
    accent: '#f59e0b',
  },
  {
    icon: 'dollar',
    title: 'Milestone Escrow Gating',
    desc: 'Safeguards transactions by holding deposits in secure escrow, released only when you approve.',
    accent: '#ec4899',
  },
  {
    icon: 'scale',
    title: 'Dispute Settlement',
    desc: 'Offers swift, secure dispute pathways with controlled channels to resolve issues.',
    accent: '#06b6d4',
  },
  {
    icon: 'trending-up',
    title: 'Trust Score Metric',
    desc: 'Real-time vendor ratings drive high-priority matches and maintain premium quality.',
    accent: '#84cc16',
  },
  {
    icon: 'file-text',
    title: 'Secure Platform Audit Trails',
    desc: 'Tamper-proof, secure logs track all transactions for total transparency.',
    accent: '#f97316',
  },
];

const SECURITY_ITEMS = [
  { icon: 'key', title: 'Session Token Protection', desc: 'Cryptographic rotation secures user sessions automatically.' },
  { icon: 'ban', title: 'Brute-Force Shielding', desc: 'Advanced mitigation shields login gateways from automated attacks.' },
  { icon: 'file-text', title: 'Payload screening', desc: 'Deconstructs uploads to block malicious payload executions.' },
  { icon: 'clock', title: 'Endpoint Rate Limits', desc: 'Intelligent throttling prevents gateway abuse and scanning.' },
  { icon: 'activity', title: 'Exif Metadata Stripping', desc: 'Strips background locations and author markers from uploads.' },
  { icon: 'lock', title: 'Column-Level Encryption', desc: 'Encrypts key personal identifiers at rest for absolute data privacy.' },
];

const COMPLIANCE = [
  { icon: 'globe', title: 'DPDP Act Compliance', desc: 'Comprehensive data consent, right-to-erase, and strict privacy controls built-in.' },
  { icon: 'scale', title: 'IT Act Recognition', desc: 'Legally recognized secure digital contract signing workflows.' },
  { icon: 'bank', title: 'Escrow Integration', desc: 'Regulated milestone-gated escrows keep your budgets safe.' },
  { icon: 'search', title: 'Pre-Verified Registry', desc: 'Direct pre-transaction verification against official MCA registries.' },
];

const ORBIT_TAGS = ['PAN', 'GST', 'KYC', 'JWT', 'AES', 'OTP'];

/* ─────────────────────────────────────────────
   STYLES (injected as a single <style> block)
 ───────────────────────────────────────────── */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.lp {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #020817;
  color: #ffffff;
  overflow-x: hidden;
  scroll-behavior: smooth;
}

/* ── NAV ── */
.lp-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 999;
  padding: 22px 0;
  transition: padding .3s ease, background .3s ease, border-color .3s ease;
  border-bottom: 1px solid transparent;
}
.lp-nav.scrolled {
  background: rgba(2,8,23,.94);
  backdrop-filter: blur(24px);
  border-bottom-color: rgba(99,102,241,.15);
  padding: 14px 0;
}
.lp-nav-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 28px;
  display: flex; align-items: center; gap: 24px;
}
.lp-logo {
  display: flex; align-items: center; gap: 10px; cursor: pointer; flex-shrink: 0;
}
.lp-logo-icon { color: #818cf8; }
.lp-logo-text {
  font-family: 'Space Grotesk', sans-serif; font-size: 20px; font-weight: 700;
  color: #ffffff; letter-spacing: -.5px; white-space: nowrap;
}
.lp-logo-x {
  background: linear-gradient(135deg,#818cf8,#06b6d4);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.lp-nav-links { display: flex; gap: 4px; margin-left: auto; }
.lp-nav-link {
  color: #f1f5f9; text-decoration: none; font-size: 15px; font-weight: 700;
  padding: 8px 16px; border-radius: 8px; transition: all .2s; border: none; background: none; cursor: pointer;
}
.lp-nav-link:hover { color: #ffffff; background: rgba(255,255,255,.08); }
.lp-nav-ctas { display: flex; gap: 8px; align-items: center; }
.lp-theme-toggle {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.lp-theme-toggle:hover {
  transform: scale(1.15) rotate(45deg);
  background: rgba(255, 255, 255, 0.08);
  color: #fbbf24;
  filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.6));
}
body.light-theme .lp-theme-toggle {
  color: #6366f1;
}
body.light-theme .lp-theme-toggle:hover {
  color: #4f46e5;
  background: rgba(0, 0, 0, 0.05);
  filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.4));
}
body.light-theme .lp-logo-text {
  color: #0f172a;
}
body.light-theme .lp-nav-link {
  color: #334155;
}
body.light-theme .lp-nav-link:hover {
  color: #0f172a;
  background: rgba(0, 0, 0, 0.04);
}
body.light-theme .lp-btn-ghost {
  color: #334155;
}
body.light-theme .lp-btn-ghost:hover {
  color: #0f172a;
  background: rgba(0, 0, 0, 0.04);
}
body.light-theme .lp-hero-title {
  color: #0f172a;
}
body.light-theme .lp-stat-num {
  color: #0f172a;
}
body.light-theme .lp-stat-label {
  color: #475569;
}

/* ── BUTTONS ── */
.lp-btn { border: none; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .25s; }
.lp-btn-ghost {
  background: transparent; color: #cbd5e1; font-size: 14px; font-weight: 600;
  padding: 10px 20px; border-radius: 8px;
}
.lp-btn-ghost:hover { color: #ffffff; background: rgba(255,255,255,.08); }
.lp-btn-primary {
  background: linear-gradient(135deg, #6366f1, #7c3aed, #06b6d4);
  background-size: 200% auto;
  color: #ffffff;
  font-size: 14px; font-weight: 700; padding: 11px 26px; border-radius: 9px;
  display: inline-flex; align-items: center; gap: 8px; position: relative;
  transition: all .4s cubic-bezier(0.16, 1, 0.3, 1);
}
.lp-btn-primary:hover {
  background-position: right center;
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(99,102,241,.45), 0 0 15px rgba(6,182,212,0.3);
}
.lp-btn-outline {
  background: transparent; border: 1.5px solid rgba(99,102,241,.45); color: #818cf8;
  font-size: 14px; font-weight: 700; padding: 10px 26px; border-radius: 9px;
}
.lp-btn-outline:hover { background: rgba(99,102,241,.08); border-color: #6366f1; transform: translateY(-2px); }
.lp-btn-lg { font-size: 16px !important; padding: 15px 34px !important; }
.lp-btn-arrow { transition: transform .2s; display: inline-block; }
.lp-btn-primary:hover .lp-btn-arrow { transform: translateX(5px); }

/* Pulsing glow on hero CTA */
@keyframes glow-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(99,102,241,.45); }
  50% { box-shadow: 0 0 0 12px rgba(99,102,241,0); }
}
.lp-btn-glow { animation: glow-pulse 2.5s ease infinite; }

/* ── HERO ── */
.lp-hero {
  position: relative; min-height: 100vh;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; padding: 130px 28px 90px;
}
.lp-hero-canvas {
  position: absolute; inset: 0; width: 100%; height: 100%; opacity: .55;
}
.lp-hero-grid {
  position: absolute; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(99,102,241,.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(99,102,241,.04) 1px, transparent 1px);
  background-size: 64px 64px;
}
.lp-hero-radial {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse 80% 60% at 50% 40%, rgba(99,102,241,.15) 0%, transparent 70%);
}
.lp-hero-split {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 48px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  z-index: 10;
  position: relative;
  text-align: left;
}
.lp-hero-split .lp-hero-content {
  text-align: left;
  max-width: 100%;
  margin: 0;
}
.lp-hero-content {
  position: relative; z-index: 10; text-align: center;
  max-width: 820px; margin: 0 auto;
}
.lp-hero-visual-panel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 480px;
}
.lp-hero-graphic-wrap {
  position: relative;
  width: 400px;
  height: 400px;
}
.lp-hero-image-card {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 260px;
  height: 260px;
  border-radius: 24px;
  background: rgba(13, 21, 38, 0.85);
  border: 1.5px solid rgba(99,102,241,0.35);
  box-shadow: 0 20px 48px rgba(0,0,0,0.5), 0 0 40px rgba(99,102,241,0.25);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  animation: ce-float 6s ease-in-out infinite;
}
.lp-hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.9;
  transition: transform 0.4s ease;
}
.lp-hero-image-card:hover .lp-hero-image {
  transform: scale(1.06);
}
@keyframes ce-float {
  0%, 100% { transform: translate(-50%, -50%) translateY(0px) rotate(0deg); }
  50% { transform: translate(-50%, -50%) translateY(-10px) rotate(0.5deg); }
}

/* Badge */
.lp-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(99,102,241,.18); border: 1.5px solid rgba(99,102,241,.45);
  border-radius: 100px; padding: 8px 20px; font-size: 15px; font-weight: 700;
  color: #ffffff; margin-bottom: 30px; letter-spacing: .5px;
  box-shadow: 0 0 20px rgba(99,102,241,0.2);
}
.lp-badge-dot {
  width: 8px; height: 8px; border-radius: 50%; background: #06b6d4;
  animation: dot-pulse 2s ease infinite;
}
@keyframes dot-pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50% { opacity:.4; transform:scale(.75); }
}

/* Hero title */
.lp-hero-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(38px,6.5vw,76px); font-weight: 700;
  line-height: 1.06; letter-spacing: -2.5px; margin-bottom: 26px; color: #ffffff;
}
.lp-gradient-text {
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 50%, #06b6d4 100%);
  background-size: 200%;
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  animation: grad-shift 5s ease infinite alternate;
}
@keyframes grad-shift { 0%{background-position:0%} 100%{background-position:100%} }
.lp-cursor {
  -webkit-text-fill-color: #06b6d4;
  animation: blink 1s step-end infinite;
}
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }

.lp-hero-sub {
  font-size: 21px; color: #f8fafc; line-height: 1.75;
  max-width: 640px; margin: 0 auto 42px; font-weight: 500;
  text-shadow: 0 2px 4px rgba(0,0,0,0.4);
}
.lp-hero-ctas {
  display: flex; gap: 16px; justify-content: center;
  flex-wrap: wrap; margin-bottom: 44px;
}
.lp-trust-row {
  display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;
}
.lp-trust-badge {
  font-size: 14.5px; color: #cbd5e1; font-weight: 600; display: flex; align-items: center; gap: 6px;
}
.lp-trust-badge-icon { color: #10b981; }

/* Scroll indicator */
.lp-scroll {
  position: absolute; bottom: 36px; left: 50%;
  transform: translateX(-50%); display: flex; flex-direction: column; align-items: center; gap: 8px;
  color: #475569; font-size: 11px; letter-spacing: 1px; text-transform: uppercase;
  animation: float-up 2s ease-in-out infinite;
}
@keyframes float-up {
  0%,100%{ transform:translateX(-50%) translateY(0); }
  50%{ transform:translateX(-50%) translateY(-7px); }
}
.lp-scroll-mouse {
  width: 22px; height: 36px; border: 2px solid #334155; border-radius: 11px;
  display: flex; justify-content: center; padding-top: 6px;
}
.lp-scroll-wheel {
  width: 4px; height: 8px; background: #06b6d4; border-radius: 2px;
  animation: wheel 1.6s ease-in-out infinite;
}
@keyframes wheel { 0%{transform:translateY(0);opacity:1} 100%{transform:translateY(12px);opacity:0} }

/* ── STATS ── */
.lp-stats {
  padding: 80px 28px;
  background: linear-gradient(180deg, rgba(99,102,241,.05) 0%, transparent 100%);
  border-top: 1px solid rgba(99,102,241,.1); border-bottom: 1px solid rgba(99,102,241,.1);
}
.lp-stats-grid {
  max-width: 1200px; margin: 0 auto;
  display: grid; grid-template-columns: repeat(4,1fr); gap: 24px;
}
.lp-stat-card {
  text-align: center; padding: 36px 20px;
  background: rgba(13,21,38,.7); border: 1px solid rgba(255,255,255,.07);
  border-radius: 18px; transition: all .3s;
}
.lp-stat-card:hover { transform: translateY(-5px); border-color: rgba(99,102,241,.35); box-shadow: 0 24px 48px rgba(0,0,0,.35); }
.lp-stat-icon {
  margin-bottom: 18px;
  display: flex;
  justify-content: center;
}
.lp-stat-num {
  font-family: 'Space Grotesk', sans-serif; font-size: 54px; font-weight: 700;
  color: #06b6d4; line-height: 1; margin-bottom: 10px;
}
.lp-stat-label { font-size: 14px; color: #cbd5e1; font-weight: 600; line-height: 1.5; }

/* ── SHARED SECTION ── */
.lp-section { padding: 110px 28px; }
.lp-section-alt { background: rgba(13,21,38,.5); }
.lp-container { max-width: 1200px; margin: 0 auto; }
.lp-section-hd { text-align: center; margin-bottom: 64px; }
.lp-overline {
  display: inline-block; font-size: 11.5px; font-weight: 700; letter-spacing: 3.5px;
  text-transform: uppercase; color: #818cf8; margin-bottom: 18px;
}
.lp-section-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(28px,4vw,46px); font-weight: 700;
  color: #ffffff; letter-spacing: -1.2px; margin-bottom: 18px;
}
.lp-section-sub {
  font-size: 18px; color: #cbd5e1; max-width: 580px; margin: 0 auto; line-height: 1.7;
}

/* ── HOW IT WORKS ── */
.lp-steps {
  display: grid; grid-template-columns: repeat(4,1fr);
  gap: 0; position: relative;
}
.lp-step { padding: 36px 28px; text-align: center; position: relative; }
.lp-step-num {
  font-family: 'Space Grotesk', sans-serif; font-size: 12px; font-weight: 700;
  letter-spacing: 2.5px; border: 1.5px solid; border-radius: 8px;
  padding: 4px 13px; display: inline-block; margin-bottom: 22px;
}
.lp-step-icon {
  margin-bottom: 18px;
  display: flex;
  justify-content: center;
}
.lp-step-title {
  font-family: 'Space Grotesk', sans-serif; font-size: 18px; font-weight: 700;
  color: #ffffff; margin-bottom: 14px;
}
.lp-step-desc { font-size: 16px; color: #f1f5f9; line-height: 1.65; font-weight: 500; }
.lp-step-conn {
  position: absolute; top: 58px; right: -10%; width: 20%; height: 2px;
  background: linear-gradient(90deg, currentColor, transparent); z-index: 5;
}

/* ── FEATURES ── */
.lp-features-grid {
  display: grid; grid-template-columns: repeat(4,1fr); gap: 18px;
}
.lp-feat {
  background: rgba(13, 21, 38, 0.45);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(129, 140, 248, 0.15);
  border-radius: 18px; padding: 30px 26px;
  transition: all .35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative; overflow: hidden; cursor: default;
}
.lp-feat::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(135deg, var(--ac) 0%, transparent 60%);
  opacity: 0; transition: opacity .3s;
}
.lp-feat:hover {
  transform: translateY(-5px);
  border-color: rgba(99, 102, 241, 0.45);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(99, 102, 241, 0.15);
}
.lp-feat:hover::after { opacity: .04; }
.lp-feat-icon-wrap {
  width: 54px; height: 54px; border-radius: 13px;
  border: 1px solid; display: flex; align-items: center; justify-content: center;
  margin-bottom: 22px; position: relative; z-index: 1; transition: transform .3s;
}
.lp-feat:hover .lp-feat-icon-wrap { transform: scale(1.1) rotate(-4deg); }
.lp-feat-icon { display: flex; align-items: center; justify-content: center; }
.lp-feat-title {
  font-family: 'Space Grotesk', sans-serif; font-size: 16px; font-weight: 700;
  color: #ffffff; margin-bottom: 10px; position: relative; z-index: 1;
}
.lp-feat-desc {
  font-size: 16px; color: #e2e8f0; line-height: 1.6;
  margin-bottom: 18px; position: relative; z-index: 1;
  font-weight: 500;
}
.lp-feat-bar {
  height: 2px; border-radius: 1px; width: 32px;
  position: relative; z-index: 1; transition: width .4s ease;
}
.lp-feat:hover .lp-feat-bar { width: 100%; }

/* ── SECURITY ── */
.lp-security-layout {
  display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center;
}
.lp-security-items { display: flex; flex-direction: column; gap: 14px; margin-top: 36px; }
.lp-sec-item {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 18px; background: rgba(13,21,38,.5);
  border: 1px solid rgba(255,255,255,.07); border-radius: 13px; transition: all .2s;
}
.lp-sec-item:hover { border-color: rgba(99,102,241,.3); background: rgba(99,102,241,.04); }
.lp-sec-icon { flex-shrink: 0; margin-top: 2px; display: flex; justify-content: center; }
.lp-sec-title {
  font-family: 'Space Grotesk', sans-serif; font-size: 15px; font-weight: 700;
  color: #ffffff; display: block; margin-bottom: 5px;
}
.lp-sec-desc { font-size: 15.5px; color: #e2e8f0; line-height: 1.55; font-weight: 500; }

/* Shield visual */
.lp-shield-wrap { display: flex; align-items: center; justify-content: center; }
.lp-shield-box { position: relative; width: 360px; height: 360px; }
.lp-ring {
  position: absolute; border-radius: 50%; border: 1px solid;
  top: 50%; left: 50%; transform: translate(-50%,-50%);
}
.lp-ring-1 {
  width:360px; height:360px;
  border-color:rgba(99,102,241,.3); border-style:dashed;
  filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.35));
  animation: spin 22s linear infinite;
}
.lp-ring-2 {
  width:270px; height:270px;
  border-color:rgba(6,182,212,.25);
  filter: drop-shadow(0 0 10px rgba(6, 182, 212, 0.3));
  animation: spin 16s linear infinite reverse;
}
.lp-ring-3 {
  width:188px; height:188px;
  border-color:rgba(124,58,237,.4);
  filter: drop-shadow(0 0 12px rgba(124, 58, 237, 0.45));
  animation: spin 11s linear infinite;
}
@keyframes spin { from{transform:translate(-50%,-50%) rotate(0deg)} to{transform:translate(-50%,-50%) rotate(360deg)} }
.lp-shield-core {
  position: absolute; top:50%; left:50%; transform:translate(-50%,-50%);
  text-align:center; background:rgba(13,21,38,.97); border:1px solid rgba(99,102,241,.35);
  border-radius:50%; width:118px; height:118px;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  box-shadow:0 0 50px rgba(99,102,241,.25), inset 0 0 30px rgba(99,102,241,.06);
}
.lp-shield-core-icon { color: #06b6d4; margin-bottom: 6px; }
.lp-shield-label { font-size:11px; font-weight:700; color:#818cf8; letter-spacing:1.5px; line-height:1.3; }
.lp-orbit-tag {
  position:absolute; transform:translate(-50%,-50%);
  background:rgba(13,21,38,.97); border:1px solid rgba(99,102,241,.35);
  border-radius:8px; padding:5px 11px; font-size:11px; font-weight:700;
  color:#818cf8; letter-spacing:1.5px; white-space:nowrap;
  animation:orbit-float 3s ease-in-out infinite;
}
@keyframes orbit-float {
  0%,100%{transform:translate(-50%,-50%) translateY(0)}
  50%{transform:translate(-50%,-50%) translateY(-6px)}
}

/* ── COMPLIANCE ── */
.lp-compliance { padding: 64px 28px; background: rgba(99,102,241,.04); border-top: 1px solid rgba(99,102,241,.12); border-bottom: 1px solid rgba(99,102,241,.12); }
.lp-compliance-inner { max-width:1200px; margin:0 auto; display:flex; align-items:center; gap:48px; flex-wrap:wrap; }
.lp-compliance-title { font-family:'Space Grotesk',sans-serif; font-size:24px; font-weight:700; color:#ffffff; white-space:nowrap; flex-shrink:0; }
.lp-compliance-list { display:flex; gap:18px; flex-wrap:wrap; flex:1; }
.lp-comp-badge {
  display:flex; align-items:center; gap:13px; padding:18px 22px;
  background:rgba(13,21,38,.85); border:1px solid rgba(255,255,255,.08);
  border-radius:14px; flex:1; min-width:180px; transition:all .2s;
}
.lp-comp-badge:hover { border-color:rgba(99,102,241,.3); }
.lp-comp-badge-icon { flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.lp-comp-badge strong { display:block; font-size:14.5px; font-weight:700; color:#ffffff; margin-bottom:3px; }
.lp-comp-badge small { display:block; font-size:15px; color:#e2e8f0; line-height:1.45; font-weight: 500; }

/* ── CTA ── */
.lp-cta { padding:110px 28px; position:relative; overflow:hidden; }
.lp-cta-bg {
  position:absolute; inset:0; pointer-events:none;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(99,102,241,.1) 0%, rgba(124,58,237,.08) 40%, transparent 70%);
}
.lp-cta-content { position:relative; z-index:1; text-align:center; }
.lp-cta-title { font-family:'Space Grotesk',sans-serif; font-size:clamp(32px,5vw,56px); font-weight:700; color:#ffffff; letter-spacing:-1.5px; margin-bottom:20px; }
.lp-cta-sub { font-size:22px; color:#f8fafc; margin-bottom:64px; font-weight: 600; text-shadow: 0 2px 4px rgba(0,0,0,0.4); }
.lp-cta-cards { display:flex; align-items:center; gap:40px; justify-content:center; flex-wrap:wrap; }
.lp-cta-card {
  background:rgba(13,21,38,.9); border:1px solid rgba(255,255,255,.1);
  border-radius:22px; padding:44px 38px; max-width:360px;
  text-align:center; transition:all .3s;
}
.lp-cta-card:hover { transform:translateY(-5px); border-color:rgba(99,102,241,.35); box-shadow:0 24px 48px rgba(0,0,0,.35); }
.lp-cta-card h3 { font-family:'Space Grotesk',sans-serif; font-size:24px; font-weight:700; color:#ffffff; margin-bottom:14px; }
.lp-cta-card p { font-size:16.5px; color:#e2e8f0; line-height:1.7; margin-bottom:30px; font-weight: 500; }
.lp-cta-div { font-size:14px; font-weight:700; color:#334155; letter-spacing:2px; }

/* ── FOOTER ── */
.lp-footer { background:rgba(2,8,23,.99); border-top:1px solid rgba(255,255,255,.06); padding:64px 28px 32px; }
.lp-footer-top { max-width:1200px; margin:0 auto; display:flex; gap:80px; margin-bottom:52px; flex-wrap:wrap; }
.lp-footer-brand { flex:0 0 240px; }
.lp-footer-tagline { font-size:15.5px; color:#cbd5e1; line-height:1.75; margin:12px 0 10px; font-weight: 500; }
.lp-footer-built { font-size:13.5px; color:#475569; }
.lp-footer-built strong { color:#94a3b8; }
.lp-footer-links { display:flex; gap:60px; flex:1; flex-wrap:wrap; }
.lp-footer-col { display:flex; flex-direction:column; gap:14px; }
.lp-footer-col h4 { font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:700; letter-spacing:2.5px; text-transform:uppercase; color:#475569; margin-bottom:2px; }
.lp-footer-col a { font-size:15.5px; color:#cbd5e1; text-decoration:none; transition:color .2s; cursor:pointer; font-weight: 500; }
.lp-footer-col a:hover { color:#06b6d4; }
.lp-footer-bottom {
  max-width:1200px; margin:0 auto; border-top:1px solid rgba(255,255,255,.05);
  padding-top:28px; display:flex; justify-content:space-between; align-items:center;
  font-size:14px; color:#475569; flex-wrap:wrap; gap:12px;
}
.lp-footer-comp { display:flex; gap:24px; }

/* ── ANIMATION ── */
.lp-animate { opacity:0; transform:translateY(26px); transition:opacity .65s ease, transform .65s ease; }
.lp-visible  { opacity:1; transform:translateY(0); }

/* ── RESPONSIVE ── */
@media (max-width:1100px) {
  .lp-hero-split {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 32px;
  }
  .lp-hero-split .lp-hero-content {
    text-align: center;
    max-width: 700px;
    margin: 0 auto;
  }
  .lp-hero-visual-panel {
    height: 380px;
    margin-top: 20px;
  }
  .lp-hero-graphic-wrap {
    width: 320px;
    height: 320px;
  }
  .lp-ring-1 { width: 320px; height: 320px; }
  .lp-ring-2 { width: 250px; height: 250px; }
  .lp-ring-3 { width: 170px; height: 170px; }
  .lp-hero-image-card { width: 200px; height: 200px; }

  .lp-features-grid { grid-template-columns:repeat(2,1fr); }
  .lp-steps          { grid-template-columns:repeat(2,1fr); }
  .lp-step-conn      { display:none; }
  .lp-security-layout{ grid-template-columns:1fr; }
  .lp-shield-wrap    { display:none; }
}
@media (max-width:1200px) {
  .lp-stats-grid { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:640px) {
  .lp-features-grid   { grid-template-columns:1fr; }
  .lp-steps           { grid-template-columns:1fr; }
  .lp-stats-grid      { grid-template-columns:repeat(2,1fr); }
  .lp-nav-links       { display:none; }
  .lp-hero-title      { letter-spacing:-1px; }
  .lp-compliance-inner{ flex-direction:column; }
  .lp-compliance-title{ white-space:normal; }
  .lp-footer-top      { flex-direction:column; gap:40px; }
  .lp-footer-links    { flex-wrap:wrap; gap:32px; }
}

/* ── Ra.One H.A.R.T. Reactor Core styles ── */
.lp-hart-overlay {
  position: absolute;
  inset: 0;
  background: rgba(8, 13, 26, 0.93);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.lp-hero-image-card:hover .lp-hart-overlay {
  opacity: 1;
  pointer-events: auto;
}
.lp-hart-core {
  width: 100%;
  height: 100%;
  max-width: 200px;
  max-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lp-hart-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.lp-hero-image-card:hover .lp-hart-core {
  animation: hart-power-up 3.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
}
@keyframes hart-power-up {
  0% { transform: scale(0.2); opacity: 0; filter: brightness(3) hue-rotate(0deg); }
  6% { transform: scale(1.35) skewX(-25deg); opacity: 0.95; filter: brightness(4) hue-rotate(180deg); }
  9% { transform: scale(0.85) skewX(20deg); opacity: 0.4; }
  13% { transform: scale(1.15) skewX(-8deg); opacity: 1; filter: brightness(2) hue-rotate(90deg); }
  17% { transform: scale(0.92) skewX(0deg); opacity: 0.65; }
  22% { transform: scale(1.08); opacity: 1; filter: brightness(1) hue-rotate(0deg); }
  32% { transform: scale(1) translateY(-2px); }
  45% { transform: scale(1.05) translateY(1px); filter: hue-rotate(45deg); }
  60% { transform: scale(1) translateY(-1px); }
  75% { transform: scale(1.02) translateY(0px) skewX(3deg); }
  85% { transform: scale(0.99) skewX(-2deg); }
  100% { transform: scale(1) skewX(0deg); opacity: 1; }
}
.lp-ring-spin-cw {
  transform-origin: 100px 100px;
  animation: spin-cw 12s linear infinite;
}
.lp-ring-spin-ccw {
  transform-origin: 100px 100px;
  animation: spin-ccw 8s linear infinite;
}
.lp-hart-center-pulse {
  transform-origin: 100px 100px;
  animation: center-pulse 2s ease-in-out infinite;
}
.lp-hart-pulse {
  transform-origin: 100px 100px;
  animation: glow-breathe 4s ease-in-out infinite;
}
.lp-hart-detail-spin {
  transform-origin: 100px 100px;
  animation: spin-cw 20s linear infinite;
}
.lp-hart-detail-spin-rev {
  transform-origin: 100px 100px;
  animation: spin-ccw 16s linear infinite;
}
@keyframes spin-cw {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes spin-ccw {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}
@keyframes center-pulse {
  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 4px #06b6d4); }
  50% { transform: scale(1.12); filter: drop-shadow(0 0 16px #ec4899); }
}
@keyframes glow-breathe {
  0%, 100% { opacity: 0.45; transform: scale(0.95); }
  50% { opacity: 0.85; transform: scale(1.06); }
}
.lp-particle-1 { animation: float-p1 4s ease-in-out infinite; }
.lp-particle-2 { animation: float-p2 5s ease-in-out infinite; }
.lp-particle-3 { animation: float-p3 6s ease-in-out infinite; }
@keyframes float-p1 {
  0%, 100% { transform: translate(0, 0); opacity: 0.4; }
  50% { transform: translate(6px, -10px); opacity: 1; }
}
@keyframes float-p2 {
  0%, 100% { transform: translate(0, 0); opacity: 0.3; }
  50% { transform: translate(-10px, 8px); opacity: 0.9; }
}
@keyframes float-p3 {
  0%, 100% { transform: translate(0, 0); opacity: 0.5; }
  50% { transform: translate(5px, 5px); opacity: 1; }
}

/* ── LANDING PAGE COMPREHENSIVE LIGHT THEME OVERRIDES ── */
body.light-theme .lp {
  background: #f8fafc;
  color: #0f172a;
}
body.light-theme .lp-nav.scrolled {
  background: rgba(255, 255, 255, 0.94);
  border-bottom-color: rgba(99, 102, 241, 0.12);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
}
body.light-theme .lp-nav-link {
  color: #475569;
}
body.light-theme .lp-nav-link:hover {
  color: #0f172a;
  background: rgba(99, 102, 241, 0.08);
}
body.light-theme .lp-logo-text {
  color: #0f172a;
}
body.light-theme .lp-btn-ghost {
  color: #475569;
}
body.light-theme .lp-btn-ghost:hover {
  color: #0f172a;
  background: rgba(99, 102, 241, 0.08);
}
body.light-theme .lp-badge {
  background: rgba(99, 102, 241, 0.08);
  border-color: rgba(99, 102, 241, 0.3);
  color: #4f46e5;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.05);
}
body.light-theme .lp-hero-title {
  color: #0f172a;
}
body.light-theme .lp-hero-sub {
  color: #475569;
}
body.light-theme .lp-stat-num {
  color: #0f172a;
}
body.light-theme .lp-stat-label {
  color: #64748b;
}
body.light-theme .lp-section-title {
  color: #0f172a;
}
body.light-theme .lp-section-sub {
  color: #475569;
}
body.light-theme .lp-section-alt {
  background: #f1f5f9;
}
body.light-theme .lp-step-num {
  background: #ffffff;
  color: #6366f1;
  border-color: rgba(99, 102, 241, 0.2);
  box-shadow: 0 8px 16px rgba(99, 102, 241, 0.08);
}
body.light-theme .lp-step-title {
  color: #0f172a;
}
body.light-theme .lp-step-desc {
  color: #475569;
}
body.light-theme .lp-step-conn {
  background: linear-gradient(90deg, rgba(99, 102, 241, 0.25), transparent);
}
body.light-theme .lp-feat {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
}
body.light-theme .lp-feat:hover {
  border-color: rgba(99, 102, 241, 0.4);
  box-shadow: 0 15px 35px rgba(99, 102, 241, 0.08), 0 0 20px rgba(99, 102, 241, 0.05);
}
body.light-theme .lp-feat-title {
  color: #0f172a;
}
body.light-theme .lp-feat-desc {
  color: #475569;
}
body.light-theme .lp-sec-title {
  color: #0f172a;
}
body.light-theme .lp-sec-desc {
  color: #475569;
}
body.light-theme .lp-sec-item {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.12);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.01);
}
body.light-theme .lp-sec-item:hover {
  border-color: rgba(99, 102, 241, 0.35);
  background: rgba(99, 102, 241, 0.02);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.05);
}
body.light-theme .lp-sec-item-title {
  color: #0f172a;
}
body.light-theme .lp-sec-item-desc {
  color: #475569;
}
body.light-theme .lp-shield-core {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.2);
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.08);
}
body.light-theme .lp-shield-label {
  color: #475569;
}
body.light-theme .lp-orbit-tag {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.2);
  color: #475569;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
}
body.light-theme .lp-compliance {
  background: #f1f5f9;
  border-top-color: rgba(99, 102, 241, 0.1);
  border-bottom-color: rgba(99, 102, 241, 0.1);
}
body.light-theme .lp-compliance-title {
  color: #0f172a;
}
body.light-theme .lp-comp-badge {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.12);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
}
body.light-theme .lp-comp-badge strong {
  color: #0f172a;
}
body.light-theme .lp-comp-badge small {
  color: #64748b;
}
body.light-theme .lp-cta-title {
  color: #0f172a;
}
body.light-theme .lp-cta-sub {
  color: #475569;
  text-shadow: none;
}
body.light-theme .lp-cta-card {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.15);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
}
body.light-theme .lp-cta-card:hover {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.08);
}
body.light-theme .lp-cta-card h3 {
  color: #0f172a;
}
body.light-theme .lp-cta-card p {
  color: #475569;
}
body.light-theme .lp-cta-div {
  color: #64748b;
}
body.light-theme .lp-footer {
  background: #f1f5f9;
  border-top-color: rgba(99, 102, 241, 0.1);
}
body.light-theme .lp-footer-tagline {
  color: #475569;
}
body.light-theme .lp-footer-col h4 {
  color: #64748b;
}
body.light-theme .lp-footer-col a {
  color: #475569;
}
body.light-theme .lp-footer-col a:hover {
  color: #6366f1;
}
body.light-theme .lp-footer-built {
  color: #64748b;
}
body.light-theme .lp-footer-built strong {
  color: #475569;
}
body.light-theme .lp-footer-bottom {
  border-top-color: rgba(99, 102, 241, 0.1);
  color: #64748b;
}
body.light-theme .lp-hero-visual-panel .lp-ring {
  border-color: rgba(99, 102, 241, 0.1) !important;
}
body.light-theme .lp-hero-image-card {
  background: #ffffff;
  border-color: rgba(99, 102, 241, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.06), 0 0 25px rgba(99, 102, 241, 0.1);
}
body.light-theme .lp-stats {
  background: linear-gradient(180deg, rgba(99,102,241,.03) 0%, transparent 100%);
  border-top-color: rgba(99,102,241,.08);
  border-bottom-color: rgba(99,102,241,.08);
}
body.light-theme .lp-stat-card {
  background: #ffffff;
  border-color: rgba(99,102,241,.12);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
}
body.light-theme .lp-stat-card:hover {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 15px 35px rgba(99, 102, 241, 0.08);
}
body.light-theme .lp-trust-badge {
  color: #475569;
}
body.light-theme .lp-hero-radial {
  background: radial-gradient(ellipse 80% 60% at 50% 40%, rgba(99,102,241,.07) 0%, transparent 70%);
}
body.light-theme .lp-hart-overlay {
  background: rgba(255, 255, 255, 0.93);
}
body.light-theme .lp-hart-center-pulse {
  fill: #ffffff;
}
}
`;

/* ─────────────────────────────────────────────
   COMPONENT
 ───────────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [typed, setTyped] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const [themeMode, setThemeMode] = useState(localStorage.getItem('cmp_theme_mode') || 'dark');

  const toggleTheme = () => {
    const nextMode = themeMode === 'dark' ? 'light' : 'dark';
    localStorage.setItem('cmp_theme_mode', nextMode);
    setThemeMode(nextMode);
    window.dispatchEvent(new Event('cmp-theme-change'));
  };

  useEffect(() => {
    const syncTheme = () => {
      setThemeMode(localStorage.getItem('cmp_theme_mode') || 'dark');
    };
    window.addEventListener('cmp-theme-change', syncTheme);
    return () => window.removeEventListener('cmp-theme-change', syncTheme);
  }, []);

  /* ── Typewriter ── */
  useEffect(() => {
    const phrase = TYPEWRITER_PHRASES[phraseIdx];
    let t;
    if (!deleting && typed === phrase) {
      t = setTimeout(() => setDeleting(true), 2200);
    } else if (deleting && typed === '') {
      setDeleting(false);
      setPhraseIdx(i => (i + 1) % TYPEWRITER_PHRASES.length);
    } else {
      t = setTimeout(
        () => setTyped(deleting ? phrase.slice(0, typed.length - 1) : phrase.slice(0, typed.length + 1)),
        deleting ? 45 : 80,
      );
    }
    return () => clearTimeout(t);
  }, [typed, deleting, phraseIdx]);

  /* ── Scroll handler ── */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* ── Intersection observer (fade-in) ── */
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('lp-visible'); }),
      { threshold: 0.08 },
    );
    document.querySelectorAll('.lp-animate').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── Count-up ── */
  useEffect(() => {
    const els = document.querySelectorAll('.lp-stat-num[data-target]');
    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = +el.dataset.target;
        const suffix = el.dataset.suffix || '';
        const dur = 1800;
        const start = performance.now();
        const tick = now => {
          const p = Math.min((now - start) / dur, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(ease * target) + suffix;
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.unobserve(el);
      });
    }, { threshold: 0.5 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ── Canvas particle network ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });

    const N = 90;
    const MAX_DIST = 155;
    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 1.8 + 0.7,
      o: Math.random() * 0.45 + 0.15,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99,102,241,${p.o})`;
        ctx.fill();
      });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < MAX_DIST) {
            const alpha = (1 - d / MAX_DIST) * 0.28;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(99,102,241,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  /* ── Helpers ── */
  const go = useCallback(path => navigate(path), [navigate]);

  return (
    <>
      <style>{STYLES}</style>
      <div className="lp">

        {/* ════ NAV ════ */}
        <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`}>
          <div className="lp-nav-inner">
            <div className="lp-logo" onClick={() => go('/')}>
              <Icon name="shield" className="lp-logo-icon" size={24} />
              <span className="lp-logo-text">Cyber<span className="lp-logo-x">Exchange</span></span>
            </div>
            <div className="lp-nav-links">
              {['How It Works', 'Features', 'Security'].map(label => (
                <a
                  key={label}
                  className="lp-nav-link"
                  href={`#${label.toLowerCase().replace(/ /g, '-')}`}
                >
                  {label}
                </a>
              ))}
            </div>
            <div className="lp-nav-ctas">
              <button 
                className="lp-theme-toggle" 
                onClick={toggleTheme} 
                title={themeMode === 'dark' ? 'Activate Light Mode' : 'Activate Dark Mode'}
              >
                <Icon name={themeMode === 'dark' ? 'sun' : 'moon'} size={20} />
              </button>
              <button className="lp-btn lp-btn-ghost" onClick={() => go('/login')}>Sign In</button>
              <button className="lp-btn lp-btn-primary" onClick={() => go('/register')}>
                Get Started <span className="lp-btn-arrow">→</span>
              </button>
            </div>
          </div>
        </nav>

        {/* ════ HERO ════ */}
        <section className="lp-hero">
          <canvas ref={canvasRef} className="lp-hero-canvas" />
          <div className="lp-hero-grid" />
          <div className="lp-hero-radial" />

          <div className="lp-hero-split">
            <div className="lp-hero-content">
              <div className="lp-badge lp-animate">
                <span className="lp-badge-dot" />
                India's First Anonymous Cybersecurity Marketplace
              </div>

              <h1 className="lp-hero-title lp-animate">
                Connect Buyers With<br />
                <span className="lp-gradient-text">
                  {typed}<span className="lp-cursor">|</span>
                </span>
              </h1>

              <p className="lp-hero-sub lp-animate">
                Post cybersecurity requirements or submit proposals anonymously. Real identities stay hidden until both parties sign a legally binding contract.
              </p>

              <div className="lp-hero-ctas lp-animate">
                <button
                  className="lp-btn lp-btn-primary lp-btn-lg lp-btn-glow"
                  onClick={() => go('/register')}
                >
                  Post a Requirement <span className="lp-btn-arrow">→</span>
                </button>
                <button
                  className="lp-btn lp-btn-outline lp-btn-lg"
                  onClick={() => go('/register')}
                >
                  Find Cybersecurity Work
                </button>
              </div>

              <div className="lp-trust-row lp-animate">
                {['KYC Verified', 'AI Protected', 'DPDP Act 2023', 'Escrow Secured'].map(t => (
                  <span key={t} className="lp-trust-badge">
                    <Icon name="check" className="lp-trust-badge-icon" size={14} />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Graphic visual panel with rings and our hero illustration */}
            <div className="lp-hero-visual-panel lp-animate">
              <div className="lp-hero-graphic-wrap">
                <div className="lp-ring lp-ring-1" />
                <div className="lp-ring lp-ring-2" />
                <div className="lp-ring lp-ring-3" />
                <div className="lp-hero-image-card">
                  <img
                    className="lp-hero-image"
                    src="/hero-visual.png"
                    alt="CyberExchange Protection Engine Graphic"
                  />
                  {/* Ra.One H.A.R.T. Holographic Reactor Core Overlay */}
                  <div className="lp-hart-overlay">
                    <div className="lp-hart-core">
                      {/* Concentric rotating neon circles & HUD */}
                      <svg viewBox="0 0 200 200" className="lp-hart-svg">
                        <defs>
                          <radialGradient id="hartGlow" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
                            <stop offset="50%" stopColor="#6366f1" stopOpacity="0.5" />
                            <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                          </radialGradient>
                          <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="6" result="blur" />
                            <feMerge>
                              <feMergeNode in="blur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>
                        
                        {/* Ambient glow backing */}
                        <circle cx="100" cy="100" r="80" fill="url(#hartGlow)" className="lp-hart-pulse" />
                        
                        {/* Outer rotating ring with dashes */}
                        <circle cx="100" cy="100" r="75" stroke="#06b6d4" strokeWidth="2.5" strokeDasharray="30 15 10 15" fill="none" className="lp-ring-spin-cw" filter="url(#neonGlow)" />
                        
                        {/* Middle opposite-rotating ring */}
                        <circle cx="100" cy="100" r="62" stroke="#7c3aed" strokeWidth="1.5" strokeDasharray="12 6" fill="none" className="lp-ring-spin-ccw" filter="url(#neonGlow)" />
                        
                        {/* Inner tech HUD rings */}
                        <circle cx="100" cy="100" r="48" stroke="#ec4899" strokeWidth="2" strokeDasharray="40 10 5 10" fill="none" className="lp-ring-spin-cw" />
                        <circle cx="100" cy="100" r="40" stroke="#06b6d4" strokeWidth="1" strokeDasharray="4 4" fill="none" />
                        
                        {/* H.A.R.T. center reactor core */}
                        <circle cx="100" cy="100" r="28" fill="#080d1a" stroke="#06b6d4" strokeWidth="3" filter="url(#neonGlow)" className="lp-hart-center-pulse" />
                        
                        {/* Center core details (glowing triangles/lines) */}
                        <path d="M100 82 L108 108 L92 108 Z" fill="#ec4899" opacity="0.85" className="lp-hart-detail-spin" />
                        <path d="M100 118 L108 92 L92 92 Z" fill="#06b6d4" opacity="0.85" className="lp-hart-detail-spin-rev" />
                        
                        {/* Floating particles */}
                        <circle cx="65" cy="80" r="2" fill="#06b6d4" className="lp-particle-1" />
                        <circle cx="135" cy="120" r="2.5" fill="#ec4899" className="lp-particle-2" />
                        <circle cx="100" cy="55" r="1.5" fill="#ffffff" className="lp-particle-3" />
                      </svg>
                    </div>
                  </div>
                </div>
                {ORBIT_TAGS.map((tag, i) => {
                  const angle = (i / ORBIT_TAGS.length) * 360;
                  const rad   = angle * (Math.PI / 180);
                  const R = 155;
                  const x = 50 + (R * Math.sin(rad)) / 3.5;
                  const y = 50 - (R * Math.cos(rad)) / 3.5;
                  return (
                    <div
                      key={tag}
                      className="lp-orbit-tag"
                      style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.45}s` }}
                    >
                      {tag}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lp-scroll">
            <div className="lp-scroll-mouse"><div className="lp-scroll-wheel" /></div>
            Scroll
          </div>
        </section>

        {/* ════ STATS ════ */}
        <section className="lp-stats">
          <div className="lp-stats-grid">
            {[
              { val: 12,  suf: '+',  label: 'Integrated Service Modules', icon: 'cpu'  },
              { val: 100, suf: '%',  label: 'KYC Verified Before Transacting', icon: 'check' },
              { val: 0,   suf: ' PII', label: 'Identity Shared Before Contract', icon: 'lock' },
              { val: 4,   suf: '',   label: 'Secure KYC Registries', icon: 'globe' },
            ].map(({ val, suf, label, icon }, i) => (
              <div key={i} className="lp-stat-card lp-animate" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="lp-stat-icon">
                  <Icon name={icon} size={36} color="#06b6d4" />
                </div>
                <div className="lp-stat-num" data-target={val} data-suffix={suf}>0{suf}</div>
                <div className="lp-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ════ HOW IT WORKS ════ */}
        <section className="lp-section" id="how-it-works">
          <div className="lp-container">
            <div className="lp-section-hd lp-animate">
              <span className="lp-overline">Simple Process</span>
              <h2 className="lp-section-title">How CyberExchange Works</h2>
              <p className="lp-section-sub">
                A structured, anonymous, and secure flow — from registration to milestone release.
              </p>
            </div>
            <div className="lp-steps">
              {STEPS.map(({ step, icon, title, desc, color }, i) => (
                <div
                  key={i}
                  className="lp-step lp-animate"
                  style={{ transitionDelay: `${i * 0.12}s` }}
                >
                  <div className="lp-step-num" style={{ color, borderColor: color }}>{step}</div>
                  <div className="lp-step-icon">
                    <Icon name={icon} size={44} color={color} />
                  </div>
                  <h3 className="lp-step-title">{title}</h3>
                  <p className="lp-step-desc">{desc}</p>
                  {i < STEPS.length - 1 && (
                    <div className="lp-step-conn" style={{ color }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════ FEATURES ════ */}
        <section className="lp-section lp-section-alt" id="features">
          <div className="lp-container">
            <div className="lp-section-hd lp-animate">
              <span className="lp-overline">Platform Capabilities</span>
              <h2 className="lp-section-title">Everything You Need to Transact Safely</h2>
              <p className="lp-section-sub">
                Built from the ground up to support Indian cybersecurity service requirements.
              </p>
            </div>
            <div className="lp-features-grid">
              {FEATURES.map(({ icon, title, desc, accent }, i) => (
                <div
                  key={i}
                  className="lp-feat lp-animate"
                  style={{ '--ac': accent, transitionDelay: `${(i % 4) * 0.08}s` }}
                >
                  <div
                    className="lp-feat-icon-wrap"
                    style={{ background: `${accent}14`, borderColor: `${accent}30` }}
                  >
                    <Icon name={icon} size={26} color={accent} className="lp-feat-icon" />
                  </div>
                  <h3 className="lp-feat-title">{title}</h3>
                  <p className="lp-feat-desc">{desc}</p>
                  <div className="lp-feat-bar" style={{ background: accent }} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════ SECURITY ════ */}
        <section className="lp-section" id="security">
          <div className="lp-container">
            <div className="lp-security-layout">

              {/* Left: text + items */}
              <div className="lp-animate">
                <span className="lp-overline">Enterprise-Grade Security</span>
                <h2 className="lp-section-title" style={{ textAlign: 'left' }}>
                  Security Baked In at Every Layer
                </h2>
                <p className="lp-section-sub" style={{ textAlign: 'left', maxWidth: '100%' }}>
                  CyberExchange matches the rigorous verification standards of security professionals.
                </p>
                <div className="lp-security-items">
                  {SECURITY_ITEMS.map(({ icon, title, desc }, i) => (
                    <div key={i} className="lp-sec-item lp-animate" style={{ transitionDelay: `${i * 0.07}s` }}>
                      <div className="lp-sec-icon">
                        <Icon name={icon} size={20} color="#06b6d4" />
                      </div>
                      <div>
                        <strong className="lp-sec-title">{title}</strong>
                        <span className="lp-sec-desc">{desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: animated shield */}
              <div className="lp-shield-wrap lp-animate">
                <div className="lp-shield-box">
                  <div className="lp-ring lp-ring-1" />
                  <div className="lp-ring lp-ring-2" />
                  <div className="lp-ring lp-ring-3" />
                  <div className="lp-shield-core">
                    <Icon name="shield" className="lp-shield-core-icon" size={40} />
                    <span className="lp-shield-label">DPDP<br/>Compliant</span>
                  </div>
                  {ORBIT_TAGS.map((tag, i) => {
                    const angle = (i / ORBIT_TAGS.length) * 360;
                    const rad   = angle * (Math.PI / 180);
                    const R = 155;
                    const x = 50 + (R * Math.sin(rad)) / 3.5;
                    const y = 50 - (R * Math.cos(rad)) / 3.5;
                    return (
                      <div
                        key={tag}
                        className="lp-orbit-tag"
                        style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.45}s` }}
                      >
                        {tag}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════ COMPLIANCE ════ */}
        <section className="lp-compliance lp-animate">
          <div className="lp-compliance-inner">
            <h3 className="lp-compliance-title">Built for India's Regulatory Landscape</h3>
            <div className="lp-compliance-list">
              {COMPLIANCE.map(({ icon, title, desc }) => (
                <div key={title} className="lp-comp-badge">
                  <div className="lp-comp-badge-icon">
                    <Icon name={icon} size={28} color="#06b6d4" />
                  </div>
                  <div>
                    <strong>{title}</strong>
                    <small>{desc}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════ CTA ─── */}
        <section className="lp-cta">
          <div className="lp-cta-bg" />
          <div className="lp-container">
            <div className="lp-cta-content lp-animate">
              <h2 className="lp-cta-title">Ready to Transact Securely?</h2>
              <p className="lp-cta-sub">
                Join CyberExchange — secure, verified, and completely anonymous B2B engagements.
              </p>
              <div className="lp-cta-cards">
                <div className="lp-cta-card">
                  <h3>I'm a Buyer</h3>
                  <p>Post your project requirements anonymously and match with KYC-verified cybersecurity professionals.</p>
                  <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={() => go('/register')}>
                    Post a Requirement <span className="lp-btn-arrow">→</span>
                  </button>
                </div>
                <div className="lp-cta-div">OR</div>
                <div className="lp-cta-card">
                  <h3>I'm a Vendor</h3>
                  <p>Submit project bids anonymously. Personal and company details stay protected until digital signatures are finalized.</p>
                  <button className="lp-btn lp-btn-outline lp-btn-lg" onClick={() => go('/register')}>
                    Find Engagements <span className="lp-btn-arrow">→</span>
                  </button>
                </div>
                <div className="lp-cta-div">OR</div>
                <div className="lp-cta-card">
                  <h3>I'm an Arbitrator</h3>
                  <p>Resolve disputes, review technical evidence, and enforce digital escrow agreements under DPDP framework guidelines.</p>
                  <button className="lp-btn lp-btn-outline lp-btn-lg" onClick={() => go('/register')}>
                    Register as Arbitrator <span className="lp-btn-arrow">→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════ FOOTER ════ */}
        <footer className="lp-footer">
          <div className="lp-footer-top">
            <div className="lp-footer-brand">
              <div className="lp-logo" onClick={() => go('/')}>
                <Icon name="shield" className="lp-logo-icon" size={24} />
                <span className="lp-logo-text">Cyber<span className="lp-logo-x">Exchange</span></span>
              </div>
              <p className="lp-footer-tagline">
                India's anonymous B2B marketplace for cybersecurity services. Secure, verified, and compliant.
              </p>
              <p className="lp-footer-built">Built by <strong>SmartX Technologies</strong></p>
            </div>
            <div className="lp-footer-links">
              <div className="lp-footer-col">
                <h4>Platform</h4>
                <a onClick={() => go('/register')}>Post a Requirement</a>
                <a onClick={() => go('/register')}>Find Work</a>
                <a onClick={() => go('/login')}>Sign In</a>
              </div>
              <div className="lp-footer-col">
                <h4>Process</h4>
                <a href="#how-it-works">How It Works</a>
                <a href="#features">Features</a>
                <a href="#security">Security</a>
              </div>
              <div className="lp-footer-col">
                <h4>Compliance</h4>
                <a>DPDP Act 2023</a>
                <a>IT Act 2000</a>
                <a>KYC Policy</a>
              </div>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>© 2026 CyberExchange · SmartX Technologies</span>
            <div className="lp-footer-comp">
              <span>🇮🇳 DPDP Act 2023</span>
              <span>⚖️ IT Act 2000</span>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
